import api from './client';

export interface ChatRequest {
  message: string;
  useCodebase?: boolean;
  useSources?: boolean;
  useKnowledge?: boolean;
  useWebSearch?: boolean;
}

export interface ChatResponse {
  message: string;
  sources?: Array<{
    type: string;
    name: string;
    snippet?: string;
  }>;
  webResults?: number;
}

export interface Source {
  id: string;
  type: 'code' | 'url' | 'text' | 'document';
  name: string;
  content: string;
  metadata?: {
    path?: string;
    url?: string;
    language?: string;
    size?: number;
    // Date objects are serialized as ISO strings in JSON responses
    lastModified?: string;
  };
  // Date objects are serialized as ISO strings in JSON responses
  createdAt: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface HealthCheck {
  aiGateway: {
    configured: boolean;
    models: {
      cheap: string;
      standard: string;
      strong: string;
    };
  };
  status: string;
  message: string;
}

export const copilotApi = {
  async checkHealth(): Promise<HealthCheck> {
    const response = await api.get<HealthCheck>('copilot/health');
    return response.data;
  },

  async chat(
    message: string,
    options: {
      useCodebase?: boolean;
      useSources?: boolean;
      useKnowledge?: boolean;
      useWebSearch?: boolean;
      sessionId?: string;
    } = {},
  ): Promise<ChatResponse> {
    const { sessionId = 'default', ...rest } = options;
    // Chat involves an LLM round-trip plus optional knowledge retrieval,
    // embeddings and web search, which can take well beyond the default 30s.
    const response = await api.postWithTimeout<ChatResponse>(
      `copilot/chat?sessionId=${sessionId}`,
      {
        message,
        ...rest,
      },
      120_000,
    );
    return response.data;
  },

  async getHistory(sessionId: string = 'default'): Promise<{ messages: ChatMessage[] }> {
    const response = await api.get<{ messages: ChatMessage[] }>(
      `copilot/history/${sessionId}`,
    );
    return response.data;
  },

  async clearHistory(sessionId: string = 'default'): Promise<void> {
    await api.delete(`copilot/history/${sessionId}`);
  },

  async getSources(): Promise<{ sources: Source[] }> {
    const response = await api.get<{ sources: Source[] }>('copilot/sources');
    return response.data;
  },

  async addCodeSource(filePaths: string[]): Promise<{ sources: Source[] }> {
    const response = await api.post<{ sources: Source[] }>('copilot/sources', {
      type: 'code',
      filePaths,
    });
    return response.data;
  },

  async addUrlSource(url: string, content?: string): Promise<{ source: Source }> {
    const response = await api.post<{ source: Source }>('copilot/sources', {
      type: 'url',
      url,
      content,
    });
    return response.data;
  },

  async addTextSource(name: string, content: string): Promise<{ source: Source }> {
    const response = await api.post<{ source: Source }>('copilot/sources', {
      type: 'text',
      name,
      content,
    });
    return response.data;
  },

  async removeSource(id: string): Promise<void> {
    await api.delete(`copilot/sources/${id}`);
  },

  async clearSources(): Promise<void> {
    await api.delete('copilot/sources');
  },

  async indexCodebase(): Promise<{
    success: boolean;
    index: {
      totalFiles: number;
      totalSize: number;
      indexedAt: string;
    };
  }> {
    const response = await api.post<{
      success: boolean;
      index: {
        totalFiles: number;
        totalSize: number;
        indexedAt: string;
      };
    }>('copilot/codebase/index');
    return response.data;
  },

  async searchCodebase(
    query: string,
    limit: number = 10,
  ): Promise<{
    results: Array<{
      path: string;
      language: string;
      size: number;
      snippet: string;
    }>;
  }> {
    const response = await api.get<{
      results: Array<{
        path: string;
        size: number;
        language: string;
        snippet: string;
      }>;
    }>(`copilot/codebase/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },
};
